import { useState, type ChangeEvent, type KeyboardEvent } from "react";
import { dangerousSites } from "../../../utils/globalDropdownOptions";
import { isDevEnvironment, isPasswordValidSafe } from "@/utils/helper";
import { isDisposableEmail } from "@/lib/disposable-email/disposable-email";

export type PasswordRequirement =
  | "NONE"
  | "DEFAULT"
  | "LETTERS-DIGITS"
  | "CASED-DIGITS"
  | "CASED-DIGITS-SYMBOLS";

export interface ImportProp {
  type: "json" | "txt" | "clipboard" | "all";
  jsonAccessor?: string;
}

export interface UseTextInputFieldProps {
  type?: "email" | "password" | "url" | null | undefined;
  passwordType?: PasswordRequirement;
  currentValue?: string;
  regex?: "alphanumeric" | "alphanumeric-code" | "numbers" | "letters" | "all";
  maxChar?: number;
  minChar?: number;
  allcaps?: boolean;
  required?: boolean;
  capitalize?: "word" | "character" | "sentence" | "first" | null;
  whitelist?: string[];
  importProp?: ImportProp;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onValidated?: (valid: boolean) => void;
}

// --- Helper Functions ---

const sanitizeURL = (url: string): boolean => {
  if (!url) return true;
  try {
    const urlObject = new URL(url);
    return dangerousSites.every(
      (site: string) => !urlObject.hostname.includes(site),
    );
  } catch (error) {
    if (isDevEnvironment()) console.warn(error);
    return true;
  }
};

const checkSourceURL = (url: string, whitelist: string[]): boolean => {
  if (!url) return true;
  try {
    const urlObject = new URL(url);
    return whitelist.some(
      (site) =>
        urlObject.hostname === site || urlObject.hostname.endsWith(`.${site}`),
    );
  } catch (error) {
    if (isDevEnvironment()) console.warn(error);
    return false;
  }
};

const validateURL = (url: string): boolean => {
  const urlPattern = /^(https?:\/\/[^\s$.?#].[^\s]*)$/i;
  return urlPattern.test(url) && sanitizeURL(url);
};

function autocapitalize(
  text: string,
  mode: "word" | "character" | "sentence" | "first" = "first",
): string {
  if (!text) return "";
  switch (mode) {
    case "word":
      return text
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    case "character":
      return text.toUpperCase();
    case "sentence":
      return text
        .split(/(?<=\.)\s+/)
        .map((sentence) => sentence.charAt(0).toUpperCase() + sentence.slice(1))
        .join(" ");
    case "first":
    default:
      return text.charAt(0).toUpperCase() + text.slice(1);
  }
}

function checkForHTMLTags(input: string): boolean {
  if (!input) return false;
  const normalized = input.toLowerCase();
  const dangerousPatterns = [
    "<svg",
    "<img",
    "<script",
    "<iframe",
    "<object",
    "<embed",
    "<link",
    "<meta",
    "<style",
    "<base",
    "onload=",
    "onerror=",
    "onclick=",
    "javascript:",
    "data:",
  ];
  return dangerousPatterns.some((pattern) => normalized.includes(pattern));
}

function sanitizeInput(input: string): string {
  if (!input) return "";
  if (!checkForHTMLTags(input)) return input;
  let sanitized = input;
  sanitized = sanitized.replace(/<[^>]*>/g, "");
  sanitized = sanitized.replace(/\bon\w+\s*=\s*["']?[^"']*["']?/gi, "");
  sanitized = sanitized.replace(/\b(javascript|data)\s*:/gi, "");
  sanitized = sanitized.replace(/[<>"]/g, "");
  return sanitized.trim();
}

// --- Hook Implementation ---

export const useTextInputField = ({
  type,
  passwordType = "DEFAULT",
  currentValue = "",
  regex = "all",
  maxChar = 0,
  minChar,
  allcaps = false,
  required = false,
  capitalize = null,
  whitelist,
  importProp,
  onChange,
  onBlur,
  onValidated,
}: UseTextInputFieldProps) => {
  const inputValue = currentValue ?? "";
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [charCount, setCharCount] = useState<number>(currentValue.length);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const isPasswordType = type === "password";
  const isExceeded = maxChar > 0 && charCount > maxChar;

  const validateInput = (value: string): boolean => {
    let regexPattern: RegExp;
    switch (regex) {
      case "alphanumeric":
        regexPattern = /^[a-zA-Z0-9]*$/;
        break;
      case "alphanumeric-code":
        regexPattern = /^[a-zA-Z0-9-_]*$/;
        break;
      case "numbers":
        regexPattern = /^\d*\.?\d{0,2}$/;
        break;
      case "letters":
        regexPattern = /^[a-zA-Z\s]*$/;
        break;
      case "all":
        return true;
      default:
        regexPattern = /.*/;
        break;
    }
    return regexPattern.test(value) && (value.match(/\s/g) || []).length <= 3;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    let error = false;
    let errorText = "";
    const processedValue = allcaps ? value.toUpperCase() : value;

    if (checkForHTMLTags(processedValue)) {
      setHasError(true);
      setErrorMessage("HTML or script-like content is not allowed.");
      onValidated?.(false);
      return;
    }

    const characterCount = processedValue.length;
    setCharCount(characterCount);

    if (maxChar > 0 && processedValue.length > maxChar) return;

    if (required && !processedValue.trim()) {
      error = true;
      errorText = "This field is required";
      onValidated?.(false);
    } else if (type === "email") {
      if (!validateEmail(processedValue)) {
        error = true;
        errorText = "Please enter a valid email address";
        onValidated?.(false);
      } else if (isDisposableEmail(processedValue)) {
        error = true;
        errorText = "Temporary or disposable email addresses are not allowed.";
        onValidated?.(false);
      } else {
        onValidated?.(true);
      }
    } else if (type === "url") {
      if (processedValue.trim() !== "") {
        const urlIsValidFormat = validateURL(processedValue);
        const urlIsSafe = sanitizeURL(processedValue);
        const urlIsInWhitelist =
          whitelist && whitelist.length > 0
            ? checkSourceURL(processedValue, whitelist)
            : true;

        if (!urlIsValidFormat) {
          error = true;
          errorText = "Enter a valid URL starting with http:// or https://";
          onValidated?.(false);
        } else if (!urlIsSafe) {
          error = true;
          errorText = "This URL is unsafe. Please enter a different URL.";
          onValidated?.(false);
        } else if (!urlIsInWhitelist) {
          error = true;
          errorText =
            "This URL is not allowed. Please use a link from an approved source.";
          onValidated?.(false);
        } else {
          onValidated?.(true);
        }
      } else {
        onValidated?.(false);
      }
    } else if (
      type === "password" &&
      !isPasswordValidSafe(processedValue, minChar, passwordType)
    ) {
      error = true;
      switch (passwordType) {
        case "LETTERS-DIGITS":
          errorText = `Please enter a valid password with at least ${!!minChar && minChar > 0 ? minChar : 8} characters and numbers.`;
          break;
        case "CASED-DIGITS":
          errorText = `Please enter a valid password with at least ${!!minChar && minChar > 0 ? minChar : 8} characters and a combination of uppercase-lowercase letters and numbers.`;
          break;
        case "CASED-DIGITS-SYMBOLS":
          errorText = `Please enter a valid password with at least ${!!minChar && minChar > 0 ? minChar : 8} characters and a combination of uppercase-lowercase letters, numbers, and symbols.`;
          break;
        case "DEFAULT":
        default:
          errorText = `Please enter a valid password with at least ${!!minChar && minChar > 0 ? minChar : 8} characters.`;
          break;
      }
      onValidated?.(true);
    } else if (!validateInput(processedValue)) {
      error = true;
      errorText = "Invalid input";
      onValidated?.(true);
    }

    setHasError(error);
    setErrorMessage(errorText);

    if (capitalize) {
      onChange?.(autocapitalize(processedValue, capitalize));
    } else {
      onChange?.(processedValue);
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const char = event.key;
    if (!validateInput(char) && char.length === 1) {
      event.preventDefault();
      return;
    }
    if (char === "Enter") {
      event.preventDefault();
      onBlur?.();
    }
  };

  const handleBlurSafe = (): void => {
    if (checkForHTMLTags(inputValue)) {
      setHasError(true);
      setErrorMessage("Invalid content detected.");
      onValidated?.(false);
      return;
    }
    onBlur?.();
  };

  const applyValue = (value: string): void => {
    const cleaned = sanitizeInput(value);
    const processed = capitalize
      ? autocapitalize(cleaned, capitalize)
      : cleaned;
    setCharCount(processed.length);
    onChange?.(processed);
    setHasError(false);
    setErrorMessage("");
  };

  const selectFile = (accept: string): Promise<File> => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = accept;
      input.onchange = () => {
        if (input.files?.[0]) resolve(input.files[0]);
        else reject(new Error("No file selected"));
      };
      input.click();
    });
  };

  const handleJsonImport = async (): Promise<void> => {
    if (!importProp?.jsonAccessor) {
      setHasError(true);
      setErrorMessage("JSON accessor is required for JSON import.");
      return;
    }
    try {
      const file = await selectFile(".json");
      const text = await file.text();
      const json = JSON.parse(text);
      const value = importProp.jsonAccessor
        .split(".")
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((obj: any, key) => obj?.[key], json);

      if (value === undefined) {
        setHasError(true);
        setErrorMessage(
          `Accessor "${importProp.jsonAccessor}" not found in JSON.`,
        );
        return;
      }
      applyValue(value.toString());
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setHasError(true);
      setErrorMessage(err.message || "Failed to import JSON");
    }
  };

  const handleTextImport = async (): Promise<void> => {
    try {
      const file = await selectFile(".txt");
      const text = await file.text();
      if (!text.trim()) {
        setHasError(true);
        setErrorMessage("Text file is empty.");
        return;
      }
      const trimmed = maxChar > 0 ? text.slice(0, maxChar) : text;
      applyValue(trimmed);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setHasError(true);
      setErrorMessage(err.message || "Failed to import text");
    }
  };

  const handleClipboardImport = async (): Promise<void> => {
    try {
      const text = await navigator.clipboard.readText();
      applyValue(text);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setHasError(true);
      setErrorMessage(`Failed to read from clipboard: ${err}`);
    }
  };

  return {
    inputValue,
    hasError,
    errorMessage,
    charCount,
    showPassword,
    isPasswordType,
    isExceeded,
    handleChange,
    handleBlurSafe,
    handleKeyDown,
    togglePasswordVisibility,
    handleJsonImport,
    handleTextImport,
    handleClipboardImport,
  };
};
